import {format} from 'date-fns';

/**
 * 日付をフォーマットして表示用の文字列に変換
 */
export function formatDisplayValue(
  dateValue: string | undefined,
  withTime: boolean,
): string {
  if (!dateValue) return '';
  try {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';

    const formatString = withTime ? 'yyyy/MM/dd HH:mm' : 'yyyy/MM/dd';
    return format(date, formatString);
  } catch {
    return '';
  }
}

/**
 * 様々なフォーマットの日付文字列をDateオブジェクトに変換
 * 対応フォーマット:
 * - 20251231 → 2025/12/31
 * - 202512311430 → 2025/12/31 14:30
 * - 2025/12/31 → 2025/12/31
 * - 2025/12/31 14:30 → 2025/12/31 14:30
 * - 2025-12-31 → 2025/12/31
 * - 2025-12-31 14:30 → 2025/12/31 14:30
 */
export function parseDateString(input: string, withTime: boolean): Date | null {
  if (!input || input.trim() === '') return null;

  const trimmed = input.trim();

  // 数字のみのフォーマット: 20251231 または 202512311430
  const digitsOnly = trimmed.replace(/\D/g, '');
  if (digitsOnly.length >= 8) {
    const year = Number.parseInt(digitsOnly.slice(0, 4), 10);
    const month = Number.parseInt(digitsOnly.slice(4, 6), 10) - 1;
    const day = Number.parseInt(digitsOnly.slice(6, 8), 10);

    let hours = 0;
    let minutes = 0;

    if (withTime && digitsOnly.length >= 12) {
      hours = Number.parseInt(digitsOnly.slice(8, 10), 10);
      minutes = Number.parseInt(digitsOnly.slice(10, 12), 10);
    } else if (withTime && digitsOnly.length >= 10) {
      // 2025123114 のような場合
      hours = Number.parseInt(digitsOnly.slice(8, 10), 10);
    }

    const date = new Date(year, month, day, hours, minutes);
    if (isValidDate(date, year, month, day)) {
      return date;
    }
  }

  // スラッシュ区切り: 2025/12/31 または 2025/12/31 14:30
  const slashMatch = trimmed.match(
    /^(\d{4})\/(\d{1,2})\/(\d{1,2})(?:\s+(\d{1,2}):(\d{1,2}))?$/,
  );
  if (slashMatch?.[1] && slashMatch[2] && slashMatch[3]) {
    const year = Number.parseInt(slashMatch[1], 10);
    const month = Number.parseInt(slashMatch[2], 10) - 1;
    const day = Number.parseInt(slashMatch[3], 10);
    const hours = slashMatch[4] ? Number.parseInt(slashMatch[4], 10) : 0;
    const minutes = slashMatch[5] ? Number.parseInt(slashMatch[5], 10) : 0;

    const date = new Date(year, month, day, hours, minutes);
    if (isValidDate(date, year, month, day)) {
      return date;
    }
  }

  // ハイフン区切り: 2025-12-31 または 2025-12-31 14:30
  const hyphenMatch = trimmed.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})(?:\s+(\d{1,2}):(\d{1,2}))?$/,
  );
  if (hyphenMatch?.[1] && hyphenMatch[2] && hyphenMatch[3]) {
    const year = Number.parseInt(hyphenMatch[1], 10);
    const month = Number.parseInt(hyphenMatch[2], 10) - 1;
    const day = Number.parseInt(hyphenMatch[3], 10);
    const hours = hyphenMatch[4] ? Number.parseInt(hyphenMatch[4], 10) : 0;
    const minutes = hyphenMatch[5] ? Number.parseInt(hyphenMatch[5], 10) : 0;

    const date = new Date(year, month, day, hours, minutes);
    if (isValidDate(date, year, month, day)) {
      return date;
    }
  }

  return null;
}

/**
 * 日付が有効かどうかをチェック
 */
export function isValidDate(
  date: Date,
  year: number,
  month: number,
  day: number,
): boolean {
  return (
    !Number.isNaN(date.getTime()) &&
    date.getFullYear() === year &&
    date.getMonth() === month &&
    date.getDate() === day
  );
}

/**
 * 時刻文字列（HH:mm）をパースして時間と分を返す
 * @param timeString - "14:30"のような時刻文字列
 * @returns {hours, minutes}または不正な場合は{hours: 0, minutes: 0}
 */
export function parseTimeString(timeString: string): {
  hours: number;
  minutes: number;
} {
  if (!timeString) {
    return {hours: 0, minutes: 0};
  }

  const parts = timeString.split(':');
  const hours = Number(parts[0]) || 0;
  const minutes = Number(parts[1]) || 0;

  return {hours, minutes};
}
